import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import SyncIcon from '@mui/icons-material/Sync'
import { RiBookmarkFill } from 'react-icons/ri'
import type { IQuestionBankEntrySummary } from '@brushfire/types'
import { formatQuestionBankLastUsedLabel } from '@/features/question-bank/lib/formatQuestionBankRelativeTime'
import {
  questionBankLastUsedForLabel,
  type QuestionBankLastUsedSurveyType,
} from '@/features/question-bank/lib/questionBankLastUsedLabel'
import { libraryBookmarkIconButtonSx, libraryCardHoverSx } from '@/shared/lib/libraryCardHover'
import { QuestionBankTypeBadge } from './QuestionBankTypeBadge'
import { QuestionBankHiddenBadge } from './QuestionBankHiddenBadge'

/** Figma 6074:13379 — Question Bank card */
const CARD_BORDER = '#e2e8f0'
const QUESTION_TEXT = '#04183f'
const META_TEXT = '#92400e'

type QuestionBankCardEntry = IQuestionBankEntrySummary & {
  lastUsedSurveyType?: QuestionBankLastUsedSurveyType | null
}

type Props = Readonly<{
  entry: QuestionBankCardEntry
  canRemove: boolean
  onOpen: () => void
  onRequestRemove: () => void
}>

export function QuestionBankCard({
  entry,
  canRemove,
  onOpen,
  onRequestRemove,
}: Props) {
  const lastUsedForLabel = questionBankLastUsedForLabel(entry.lastUsedSurveyType)

  return (
    <Box
      component="article"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      data-testid={`question-bank-card-${entry.id}`}
      sx={{
        position: 'relative',
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: '12px',
        bgcolor: '#fff',
        boxShadow: 'none',
        width: '100%',
        p: '25px',
        minHeight: 176,
        boxSizing: 'border-box',
        cursor: 'pointer',
        textAlign: 'left',
        ...libraryCardHoverSx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            height: '42px',
            flexShrink: 0,
          }}
        >
          <QuestionBankTypeBadge apiType={entry.type} compact />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {entry.hidden ? <QuestionBankHiddenBadge /> : null}
            {canRemove ? (
              <IconButton
                size="small"
                aria-label="Remove from Question Bank"
                data-testid={`question-bank-remove-${entry.id}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onRequestRemove()
                }}
                sx={{
                  p: 0.5,
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  ...libraryBookmarkIconButtonSx,
                }}
              >
                <RiBookmarkFill size={20} aria-hidden />
              </IconButton>
            ) : null}
          </Box>
        </Box>

        <Typography
          component="p"
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '24px',
            color: QUESTION_TEXT,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
            m: 0,
          }}
        >
          {entry.prompt}
        </Typography>

        {lastUsedForLabel ? (
          <Typography
            component="p"
            data-testid={`question-bank-last-used-for-${entry.id}`}
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              color: '#475569',
              m: 0,
              pt: '4px',
            }}
          >
            {lastUsedForLabel}
          </Typography>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            pt: '8px',
            minHeight: '28px',
            boxSizing: 'border-box',
            color: META_TEXT,
          }}
        >
          <SyncIcon sx={{ fontSize: 14, color: META_TEXT, flexShrink: 0 }} aria-hidden />
          <Typography
            component="span"
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 400,
              color: META_TEXT,
              wordBreak: 'break-word',
              m: 0,
            }}
          >
            Used {entry.usageCount} times · Last used:{' '}
            {formatQuestionBankLastUsedLabel(entry.lastUsedAt)} · Saved by: {entry.savedByName}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
